/*
 * QuoteBuilderMockupPage
 *
 * NON-FUNCTIONAL VISUAL MOCKUP for the future AI Quote Builder feature.
 * All state is local — no backend calls, no GPT, no persistence. This page
 * exists so the user can tap through the 5-step flow on their phone and
 * approve the look/feel BEFORE we commit build credits.
 *
 * Route: /quote-builder-mockup  (temporary — remove after real feature ships)
 *
 * Design guardrails:
 *   • Mobile-first, single-column, thumb-zone primary CTAs (bottom-anchored)
 *   • 48px+ tap targets on every interactive element (touch-manipulation)
 *   • Card-based item rendering (never a mobile table)
 *   • Ink-friendly aesthetic that matches the rest of the site (brand
 *     charcoal / gold / teal / cream palette from tailwind config)
 */
import { useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronRight,
  Loader2,
  Minus,
  Plus,
  Sparkles,
  Trash2,
  Utensils,
  Users,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";

// ── Static demo data — the real feature will read from /api/menu ───────────
const DIETARY_TAGS = [
  { id: "halal", label: "Halal", emoji: "🕌" },
  { id: "vegetarian", label: "Vegetarian", emoji: "🥗" },
  { id: "vegan", label: "Vegan", emoji: "🌱" },
  { id: "gluten_free", label: "Gluten-Free", emoji: "🌾" },
  { id: "nut_free", label: "Nut-Free", emoji: "🥜" },
  { id: "kid_friendly", label: "Kid-Friendly", emoji: "🧒" },
];

const SAMPLE_AI_DRAFT = [
  {
    id: "1", name: "Signature Angus Sliders", tray: "Full Tray (40)",
    qty: 3, unit: 240, notes: "Cheese: American · Toasted brioche buns",
    ai_reason: "Crowd-pleaser for a mixed-age group of 120.",
  },
  {
    id: "2", name: "Halal Grilled Chicken Skewers", tray: "Full Tray (30)",
    qty: 4, unit: 195, notes: "Marinated · Served with tzatziki",
    ai_reason: "Halal option — covers the dietary requirement.",
  },
  {
    id: "3", name: "Vegetarian Grain Bowl Platter", tray: "12-serve",
    qty: 3, unit: 89, notes: "Quinoa, roasted veg, tahini",
    ai_reason: "Vegetarian anchor — safe for most dietary flags.",
  },
  {
    id: "4", name: "Truffle Parmesan Fries", tray: "Family Tray",
    qty: 4, unit: 55, notes: "Shareable side",
    ai_reason: "Side that scales cheaply for a large group.",
  },
  {
    id: "5", name: "Assorted Cookie Platter", tray: "24-count",
    qty: 5, unit: 42, notes: "Chocolate chip, oatmeal raisin, snickerdoodle",
    ai_reason: "Dessert — one cookie per head.",
  },
];

const STEPS = ["event", "contact", "generating", "review", "done"];

export default function QuoteBuilderMockupPage() {
  const [step, setStep] = useState("event");

  // Step 1: Event intake
  const [eventDesc, setEventDesc] = useState(
    "SYEP graduation ceremony — mixed teens, need halal options",
  );
  const [headcount, setHeadcount] = useState("120");
  const [budget, setBudget] = useState("2500");
  const [dietary, setDietary] = useState(new Set(["halal", "vegetarian"]));
  const [zip, setZip] = useState("10455");
  const [eventDate, setEventDate] = useState("2026-08-26");

  // Step 2: Contact soft-gate
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // Step 4: Editable draft
  const [items, setItems] = useState(SAMPLE_AI_DRAFT);

  const toggleDietary = (id) => {
    setDietary((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const bump = (id, delta) => {
    setItems((prev) =>
      prev
        .map((it) =>
          it.id === id ? { ...it, qty: Math.max(0, it.qty + delta) } : it,
        )
        .filter((it) => it.qty > 0),
    );
  };
  const removeItem = (id) =>
    setItems((prev) => prev.filter((it) => it.id !== id));

  const subtotal = useMemo(
    () => items.reduce((sum, it) => sum + it.qty * it.unit, 0),
    [items],
  );

  const goNext = () => {
    if (step === "event") setStep("contact");
    else if (step === "contact") {
      setStep("generating");
      setTimeout(() => setStep("review"), 2200);
    } else if (step === "review") setStep("done");
  };
  const goBack = () => {
    if (step === "contact") setStep("event");
    else if (step === "review") setStep("contact");
  };

  const stepIndex = STEPS.indexOf(step);
  const progressPct = ((stepIndex + 1) / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-brand-cream/40 flex flex-col">
      {/* ── HEADER ───────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-20 bg-white/95 backdrop-blur border-b border-black/10">
        <div className="mx-auto max-w-xl px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-9 w-9 shrink-0 rounded-full bg-brand-charcoal text-brand-gold inline-flex items-center justify-center">
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-black uppercase tracking-[0.16em] text-brand-teal">
                Fresh Fast Foods
              </div>
              <div className="text-[14px] font-black text-brand-charcoal truncate">
                AI Quote Builder
              </div>
            </div>
          </div>
          {step !== "done" && (
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/60">
              Step {stepIndex + 1} of {STEPS.length - 1}
            </div>
          )}
        </div>
        <div className="h-1 bg-black/5">
          <div
            className="h-full bg-brand-gold transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </header>

      {/* ── BODY ─────────────────────────────────────────────────────── */}
      <main className="flex-1 mx-auto w-full max-w-xl px-5 py-6 pb-32">
        {step === "event" && (
          <EventStep
            {...{
              eventDesc, setEventDesc, headcount, setHeadcount,
              budget, setBudget, zip, setZip, eventDate, setEventDate,
              dietary, toggleDietary,
            }}
          />
        )}
        {step === "contact" && (
          <ContactStep {...{ name, setName, email, setEmail, phone, setPhone }} />
        )}
        {step === "generating" && <GeneratingStep />}
        {step === "review" && (
          <ReviewStep
            {...{ items, bump, removeItem, subtotal, headcount, eventDate }}
          />
        )}
        {step === "done" && <DoneStep {...{ email, name }} />}
      </main>

      {/* ── STICKY THUMB-ZONE CTA ────────────────────────────────────── */}
      {step !== "generating" && step !== "done" && (
        <div className="sticky bottom-0 z-20 bg-white border-t-2 border-brand-gold/40 shadow-[0_-8px_24px_-12px_rgba(0,0,0,0.15)]">
          <div className="mx-auto max-w-xl px-5 py-3">
            {step === "review" && (
              <div className="flex items-center justify-between mb-2 px-1">
                <div className="text-[10px] font-black uppercase tracking-[0.14em] text-brand-charcoal/60">
                  Estimated subtotal
                </div>
                <div className="text-[20px] font-black text-brand-charcoal">
                  ${subtotal.toLocaleString()}
                </div>
              </div>
            )}
            <div className="flex gap-3">
              {step !== "event" && (
                <Button
                  variant="outline"
                  onClick={goBack}
                  data-testid="mockup-back-btn"
                  className="min-h-[52px] px-5 rounded-full border-2 border-brand-charcoal/20 text-brand-charcoal"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              )}
              <Button
                onClick={goNext}
                data-testid="mockup-next-btn"
                className="flex-1 min-h-[52px] rounded-full bg-brand-charcoal hover:bg-brand-teal text-white text-[13px] font-black uppercase tracking-[0.12em]"
              >
                {step === "event" && "Continue"}
                {step === "contact" && (
                  <>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Generate My Quote
                  </>
                )}
                {step === "review" && "Submit for Review"}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// STEP 1 — Event Intake
// ═══════════════════════════════════════════════════════════════════════
function EventStep({
  eventDesc, setEventDesc, headcount, setHeadcount,
  budget, setBudget, zip, setZip, eventDate, setEventDate,
  dietary, toggleDietary,
}) {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[26px] font-black leading-tight text-brand-charcoal">
          Tell us about your event
        </h1>
        <p className="mt-2 text-[14px] text-brand-charcoal/70">
          The more detail, the better the AI can tailor your menu.
        </p>
      </div>

      <div className="space-y-2">
        <Label className="text-[11px] font-black uppercase tracking-[0.14em] text-brand-teal">
          What's happening?
        </Label>
        <Textarea
          value={eventDesc}
          onChange={(e) => setEventDesc(e.target.value)}
          rows={3}
          placeholder="e.g., Team offsite for 40, most people are vegetarian…"
          data-testid="mockup-event-desc"
          className="text-[15px] min-h-[92px] rounded-2xl border-2 border-black/10 focus:border-brand-teal p-4"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <BigNumberInput
          icon={<Users className="h-4 w-4" />}
          label="Headcount"
          value={headcount}
          onChange={setHeadcount}
          suffix="people"
          testid="mockup-headcount"
        />
        <BigNumberInput
          icon="$"
          label="Budget (optional)"
          value={budget}
          onChange={setBudget}
          suffix=""
          testid="mockup-budget"
        />
      </div>

      <div className="space-y-3">
        <Label className="text-[11px] font-black uppercase tracking-[0.14em] text-brand-teal">
          Dietary needs — tap any that apply
        </Label>
        <div className="flex flex-wrap gap-2">
          {DIETARY_TAGS.map((t) => {
            const active = dietary.has(t.id);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => toggleDietary(t.id)}
                data-testid={`mockup-dietary-${t.id}`}
                className={`min-h-[48px] px-4 rounded-full text-[13px] font-bold border-2 transition-colors touch-manipulation ${
                  active
                    ? "bg-brand-charcoal text-white border-brand-charcoal"
                    : "bg-white text-brand-charcoal border-black/10"
                }`}
              >
                <span className="mr-2">{t.emoji}</span>
                {t.label}
                {active && <Check className="h-3.5 w-3.5 inline-block ml-2" />}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-2">
          <Label className="text-[11px] font-black uppercase tracking-[0.14em] text-brand-teal">
            Event date
          </Label>
          <Input
            type="date"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            data-testid="mockup-date"
            className="text-[15px] min-h-[52px] rounded-2xl border-2 border-black/10 px-4"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[11px] font-black uppercase tracking-[0.14em] text-brand-teal">
            Delivery ZIP
          </Label>
          <Input
            inputMode="numeric"
            maxLength={5}
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
            placeholder="10455"
            data-testid="mockup-zip"
            className="text-[15px] min-h-[52px] rounded-2xl border-2 border-black/10 px-4 font-mono"
          />
        </div>
      </div>
    </div>
  );
}

function BigNumberInput({ icon, label, value, onChange, suffix, testid }) {
  return (
    <div className="space-y-2">
      <Label className="text-[11px] font-black uppercase tracking-[0.14em] text-brand-teal">
        {label}
      </Label>
      <div className="relative">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-charcoal/60">
          {icon}
        </div>
        <Input
          inputMode="numeric"
          value={value}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, ""))}
          data-testid={testid}
          className="text-[16px] min-h-[52px] rounded-2xl border-2 border-black/10 pl-11 pr-4 font-bold"
        />
        {suffix && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2 text-[11px] font-bold text-brand-charcoal/40">
            {suffix}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// STEP 2 — Soft-Gate Contact
// ═══════════════════════════════════════════════════════════════════════
function ContactStep({ name, setName, email, setEmail, phone, setPhone }) {
  return (
    <div className="space-y-6">
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-gold/20 text-brand-charcoal text-[10px] font-black uppercase tracking-[0.14em] mb-3">
          <Sparkles className="h-3 w-3" />
          Almost there
        </div>
        <h1 className="text-[26px] font-black leading-tight text-brand-charcoal">
          Where should we send it?
        </h1>
        <p className="mt-2 text-[14px] text-brand-charcoal/70">
          We'll email your AI-drafted menu so you can review it later on any
          device. No spam, no marketing.
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label className="text-[11px] font-black uppercase tracking-[0.14em] text-brand-teal">
            Your name
          </Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Marcus Rivera"
            data-testid="mockup-name"
            className="text-[16px] min-h-[52px] rounded-2xl border-2 border-black/10 px-4"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[11px] font-black uppercase tracking-[0.14em] text-brand-teal">
            Email
          </Label>
          <Input
            type="email"
            inputMode="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid="mockup-email"
            className="text-[16px] min-h-[52px] rounded-2xl border-2 border-black/10 px-4"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-[11px] font-black uppercase tracking-[0.14em] text-brand-teal">
            Phone (optional)
          </Label>
          <Input
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(347) 555-0000"
            data-testid="mockup-phone"
            className="text-[16px] min-h-[52px] rounded-2xl border-2 border-black/10 px-4 font-mono"
          />
        </div>
      </div>

      <p className="text-[11px] text-brand-charcoal/50 leading-relaxed">
        By continuing you agree to be contacted by Fresh Fast Foods about this
        quote. Your info stays private and is never sold.
      </p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// STEP 3 — AI Generating (loading)
// ═══════════════════════════════════════════════════════════════════════
function GeneratingStep() {
  const stages = [
    "Reading your event details…",
    "Matching menu items to your headcount…",
    "Checking dietary requirements…",
    "Building your personalized quote…",
  ];
  const [idx, setIdx] = useState(0);
  useMemo(() => {
    // Cycle through stage labels for visual feedback
    const t = setInterval(
      () => setIdx((i) => (i < stages.length - 1 ? i + 1 : i)),
      500,
    );
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="pt-16 flex flex-col items-center text-center gap-6">
      <div className="relative">
        <div className="absolute inset-0 rounded-full bg-brand-gold/30 blur-xl animate-pulse" />
        <div className="relative h-24 w-24 rounded-full bg-brand-charcoal text-brand-gold inline-flex items-center justify-center">
          <Sparkles className="h-10 w-10 animate-pulse" />
        </div>
      </div>
      <div>
        <h1 className="text-[22px] font-black text-brand-charcoal">
          Building your quote…
        </h1>
        <p className="mt-2 text-[14px] text-brand-charcoal/70">
          {stages[idx]}
        </p>
      </div>
      <Loader2 className="h-6 w-6 animate-spin text-brand-charcoal/40" />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// STEP 4 — Editable AI Draft
// ═══════════════════════════════════════════════════════════════════════
function ReviewStep({ items, bump, removeItem, subtotal, headcount, eventDate }) {
  const totalUnits = items.reduce((s, it) => s + it.qty, 0);
  return (
    <div className="space-y-5">
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-black uppercase tracking-[0.14em] mb-3">
          <Sparkles className="h-3 w-3" />
          AI-Drafted for you
        </div>
        <h1 className="text-[24px] font-black leading-tight text-brand-charcoal">
          Your personalized quote
        </h1>
        <p className="mt-2 text-[14px] text-brand-charcoal/70">
          Tap ± to adjust portions. Everything is editable before submit.
        </p>
      </div>

      {/* Summary strip */}
      <div className="flex items-center gap-4 rounded-2xl border-2 border-brand-charcoal/10 p-3">
        <StatChip icon={<Users className="h-3.5 w-3.5" />} label="Headcount" value={headcount} />
        <div className="h-8 w-px bg-black/10" />
        <StatChip icon={<Utensils className="h-3.5 w-3.5" />} label="Trays" value={totalUnits} />
        <div className="h-8 w-px bg-black/10" />
        <StatChip label="Event" value={eventDate?.slice(5) || "—"} />
      </div>

      {/* Line-item cards */}
      <div className="space-y-3">
        {items.map((it) => (
          <div
            key={it.id}
            data-testid={`mockup-item-${it.id}`}
            className="rounded-2xl border-2 border-black/10 bg-white p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-[15px] font-black text-brand-charcoal leading-snug">
                  {it.name}
                </div>
                <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-brand-charcoal/50 mt-0.5">
                  {it.tray}
                </div>
                {it.notes && (
                  <div className="text-[12.5px] text-brand-charcoal/70 mt-1.5 leading-snug">
                    {it.notes}
                  </div>
                )}
                <div className="mt-2 flex items-start gap-1.5 text-[11.5px] text-brand-teal">
                  <Sparkles className="h-3 w-3 shrink-0 mt-0.5" />
                  <span className="italic leading-snug">{it.ai_reason}</span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeItem(it.id)}
                data-testid={`mockup-remove-${it.id}`}
                className="shrink-0 h-9 w-9 rounded-full text-brand-charcoal/40 hover:text-red-600 hover:bg-red-50 inline-flex items-center justify-center touch-manipulation"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <div className="mt-3 flex items-center justify-between pt-3 border-t border-black/5">
              <div className="text-[11px] font-black uppercase tracking-[0.12em] text-brand-charcoal/50">
                ${it.unit} × {it.qty} · <span className="text-brand-charcoal">${(it.unit * it.qty).toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => bump(it.id, -1)}
                  data-testid={`mockup-decr-${it.id}`}
                  className="h-11 w-11 rounded-full border-2 border-black/10 inline-flex items-center justify-center text-brand-charcoal touch-manipulation active:bg-black/5"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <div className="w-10 text-center text-[17px] font-black tabular-nums text-brand-charcoal">
                  {it.qty}
                </div>
                <button
                  type="button"
                  onClick={() => bump(it.id, 1)}
                  data-testid={`mockup-incr-${it.id}`}
                  className="h-11 w-11 rounded-full bg-brand-charcoal text-white inline-flex items-center justify-center touch-manipulation active:scale-95 transition-transform"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {/* Add-more prompt card */}
        <button
          type="button"
          data-testid="mockup-add-more"
          className="w-full rounded-2xl border-2 border-dashed border-brand-charcoal/25 p-4 text-brand-charcoal/70 text-[13px] font-bold inline-flex items-center justify-center gap-2 min-h-[64px] touch-manipulation hover:border-brand-teal hover:text-brand-teal"
        >
          <Plus className="h-4 w-4" />
          Add another item
        </button>
      </div>
    </div>
  );
}

function StatChip({ icon, label, value }) {
  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.14em] text-brand-charcoal/50">
        {icon}
        {label}
      </div>
      <div className="text-[16px] font-black text-brand-charcoal mt-0.5 truncate">
        {value}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// STEP 5 — Success confirmation
// ═══════════════════════════════════════════════════════════════════════
function DoneStep({ email, name }) {
  return (
    <div className="pt-10 flex flex-col items-center text-center gap-6">
      <div className="h-20 w-20 rounded-full bg-emerald-100 text-emerald-700 inline-flex items-center justify-center">
        <Check className="h-10 w-10" strokeWidth={3} />
      </div>
      <div>
        <h1 className="text-[26px] font-black text-brand-charcoal">
          Quote sent!
        </h1>
        <p className="mt-3 text-[15px] text-brand-charcoal/70 max-w-sm">
          {name ? `${name.split(" ")[0]}, ` : ""}we've emailed a copy to{" "}
          <strong>{email || "you"}</strong>. Our team will follow up within
          24 hours with a formal quote.
        </p>
      </div>
      <div className="w-full max-w-sm rounded-2xl border-2 border-black/10 p-4 text-left">
        <div className="text-[10px] font-black uppercase tracking-[0.14em] text-brand-teal">
          What happens next
        </div>
        <ol className="mt-2 space-y-2 text-[13.5px] text-brand-charcoal">
          <li className="flex gap-2"><ChevronRight className="h-4 w-4 shrink-0 text-brand-gold mt-0.5" /> Chef reviews your AI-drafted menu</li>
          <li className="flex gap-2"><ChevronRight className="h-4 w-4 shrink-0 text-brand-gold mt-0.5" /> You receive a formal quote by email</li>
          <li className="flex gap-2"><ChevronRight className="h-4 w-4 shrink-0 text-brand-gold mt-0.5" /> Approve + pay online or via PO</li>
        </ol>
      </div>
      <a href="/">
        <Button
          variant="outline"
          className="min-h-[48px] rounded-full border-2 border-brand-charcoal/20 text-brand-charcoal"
          data-testid="mockup-return-home"
        >
          Return to home
        </Button>
      </a>
    </div>
  );
}
